import React, { forwardRef, Ref, useEffect, useRef, useState } from 'react';
import {
  Button as AriaButton,
  Input,
  Label,
  TextField,
  TextFieldProps,
} from 'react-aria-components';
import { useLingui } from '@lingui/react/macro';

import Hint from '@/design_system/Hint/Hint';
import Message, { MessageProps } from '@/design_system/Message/Message';
import IconVisibility from '@/icons/Visibility.svg';
import { createBEMClasses } from '@/utils/classname';

import './InputText.css';

const { block, element } = createBEMClasses('input-text');

export interface InputTextProps extends TextFieldProps {
  label?: React.ReactNode;
  labelFontWeight?: 'regular' | 'medium';
  placeholder?: string;
  type?: 'text' | 'password' | 'email' | 'tel';
  showMessageIcon?: boolean;
  size?: 'x-small' | 'small' | 'medium' | 'large';
  hintText?: React.ReactNode;
  messageType?: MessageProps['type'];
  messageText?: string;
  isInvalid?: boolean;
  isSuccess?: boolean;
  isLoading?: boolean;
  error?: string;
  className?: string;
  edited?: boolean;
  onMount?: (ref: Ref<HTMLInputElement | null>) => void;
}

const InputText = (
  {
    label,
    labelFontWeight = 'medium',
    placeholder,
    type = 'text',
    showMessageIcon = true,
    size = 'medium',
    hintText,
    messageType,
    messageText,
    isInvalid,
    isSuccess,
    isRequired,
    isLoading,
    error,
    className,
    edited = false,
    onMount,
    ...props
  }: InputTextProps,
  ref?: Ref<HTMLInputElement>
) => {
  const { t } = useLingui();
  const isPasswordField = type === 'password';

  const [visibility, setVisibility] = useState(false);
  const [adjustedType, setAdjustedType] = useState(type);

  const internalRef = useRef<HTMLInputElement>(null);

  const resolvedRef = ref ?? internalRef;

  // This useEffect with empty dependencies array will run when the component first mounts
  useEffect(() => {
    onMount?.(resolvedRef);
  }, []);

  return (
    <TextField
      {...props}
      className={block({ size, invalid: isInvalid || !!error, success: isSuccess }, className)}
    >
      {label && (
        <Label
          className={
            size === 'large'
              ? labelFontWeight === 'medium'
                ? 'paragraph-50-medium'
                : 'paragraph-50-regular'
              : labelFontWeight === 'medium'
                ? 'label-100-medium'
                : 'label-100-regular'
          }
        >
          {label}
        </Label>
      )}
      <div
        className={element('control', {
          loading: isLoading,
        })}
      >
        <Input
          ref={resolvedRef}
          required={isRequired}
          className={element('control__input', {
            'with-visibility': isPasswordField,
            edited,
          })}
          placeholder={placeholder}
          type={adjustedType}
          size={10}
        />
        {isPasswordField && (
          <AriaButton
            className={element('control__visibility')}
            onPress={() => {
              setAdjustedType(visibility ? 'password' : 'text');
              setVisibility(!visibility);
            }}
            aria-label={t({
              id: 'design-system.input-text.toggle-password-visibility',
              message: 'Toggle password visibility',
            })}
          >
            <IconVisibility on={visibility} />
          </AriaButton>
        )}
      </div>
      {hintText && <Hint>{hintText}</Hint>}
      {(messageText || error) && (
        <Message type={error ? 'error' : messageType} showMessageIcon={showMessageIcon}>
          {messageText || error}
        </Message>
      )}
    </TextField>
  );
};

export default forwardRef(InputText);
